import { css } from '@emotion/core';
import { Select } from 'antd';
import { withRouter } from 'next/router';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setCurrentPage, setGroupBy } from '@/src/compliance/redux/complianceReducer';
import {
  getChartsArrangedByState,
  getChartsArrangedByTopicAndState,
  getPaginatedCharts,
} from '@/src/compliance/redux/selectors';
import parseURL from '@/src/compliance/utils/parseURL';
import { generateHTMLString } from '@/src/pdf/templates/compliance';
import { convertHtmlToPdf } from '@/src/pdf/utils';
import Loading from '@/src/shared/components/LoadingResults';
import Pagination from '@/src/shared/components/Pagination';
import { bp, colors, space } from '@/styles';
import { getCurrentDate } from '@/utils/dateTimeHelpers';

import PageButtons from './PageButtons';
import SearchResultsAccordion from './SearchResultsAccordion';

const { m } = space;

const style = {
  pageButtons: css`
    background-color: ${colors.white};
  `,
  paginationRow: css`
    display: flex;
    flex-wrap: wrap;
  `,
  btnRow: css`
    display: flex;
    justify-content: space-between;
  `,
  groupSelect: css`
    width: 100%;

    @media ${bp.sm} {
      width: 35%;
      padding-left: 16px;
    }
  `,
};

const SearchResults = props => {
  const dispatch = useDispatch();

  const {
    searchResults,
    searchFiltersValues,
    searchOptions: { charts: chartsOptions, states: statesOptions, categories: categoriesOptions },
  } = useSelector(state => state.compliance);

  const {
    searchResults: results = {},
    currentPage,
    isFetchingCharts,
    groupByChart,
  } = searchResults;

  const charts = results.charts || results.Charts || [];

  const fieldOptions = {
    Charts: chartsOptions ?? [],
    States: statesOptions ?? [],
    Categories: categoriesOptions ?? [],
  };

  const formattedCharts = getChartsArrangedByTopicAndState(charts);
  const formattedChartsByState = getChartsArrangedByState(charts);
  const resultsGroupedByState = getChartsArrangedByState(charts);

  const extraSearchValues = {
    count: formattedCharts.length,
    results: getPaginatedCharts(formattedCharts, currentPage),
    originalResultsFormat: charts,
    stateCount: formattedChartsByState.length,
  };

  const { count, results: paginatedResults, originalResultsFormat, stateCount } = extraSearchValues;

  const [pdfExportStatus, setPdfExportStatus] = useState(false);

  const { router, displayChanges } = props;

  const handlePageChange = pageNumber => {
    dispatch(setCurrentPage(pageNumber));
  };

  const handleGroupBy = e => {
    const groupVal = Boolean(e === 'chart');
    dispatch(setGroupBy(groupVal));
  };

  const handleExportToPdf = async () => {
    setPdfExportStatus(true);

    const updatedPayload = parseURL(fieldOptions, router);

    const selectedCategories = searchFiltersValues.categories
      ? fieldOptions.Categories.filter(cat => cat.categoryId === searchFiltersValues.categories)
      : [];

    const selectedCharts = Array.isArray(searchFiltersValues.charts)
      ? fieldOptions.Charts.filter(c => searchFiltersValues.charts.includes(c.chartId))
      : [];

    const selectedStates = Array.isArray(searchFiltersValues.states)
      ? fieldOptions.States.filter(s => searchFiltersValues.states.includes(s.stateCode))
      : [];

    const searchCriteria = {
      categories: selectedCategories,
      charts: selectedCharts,
      states: selectedStates,
      searchText: searchFiltersValues.searchText,
      ...updatedPayload,
    };

    const currentDate = getCurrentDate();
    const fileName = `Compliassist Charts - ${currentDate}.pdf`;
    const groupBy = groupByChart ? 'chart' : 'state';

    const html = generateHTMLString(
      formattedCharts,
      formattedChartsByState,
      searchCriteria,
      groupBy
    );

    await convertHtmlToPdf({ html, fileName }, setPdfExportStatus);
  };

  const pagination = (
    <Pagination
      current={currentPage}
      total={groupByChart ? count : stateCount}
      pageSize={10}
      onChange={pageNumber => {
        handlePageChange(pageNumber);
      }}
      css={css`
        margin-bottom: 24px;
      `}
    />
  );

  return (
    <div>
      <div css={style.paginationRow}>
        {pagination}

        <Select
          defaultValue="chart"
          css={[style.groupSelect]}
          onChange={handleGroupBy}
          value={groupByChart ? 'chart' : 'state'}
        >
          <Select.Option value="chart">Group By Chart</Select.Option>
          <Select.Option value="state">Group By State</Select.Option>
        </Select>
      </div>

      <PageButtons
        isDisabled={Boolean(count)}
        data={originalResultsFormat}
        pdfDownloading={pdfExportStatus}
        pdfDisabled={isFetchingCharts}
        handleExportToPdf={handleExportToPdf}
        groupBy={groupByChart ? 'chart' : 'state'}
      />

      <div css={m.t24}>
        {isFetchingCharts ? (
          <Loading height="500px" />
        ) : (
          <SearchResultsAccordion
            results={groupByChart ? paginatedResults : resultsGroupedByState}
            groupByChart={groupByChart}
            displayChanges={displayChanges}
          />
        )}
      </div>

      <div
        css={css`
          margin-top: 24px;
        `}
      >
        {pagination}
      </div>
    </div>
  );
};

export default withRouter(SearchResults);